import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.tsx';
import { dashboardPathFor } from '../auth/dashboardPath.ts';
import { ApiError } from '../api.ts';
import { Logo } from '../components/Logo.tsx';

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') ?? undefined;
  // Le parrainage ne concerne que les extras : avec un code, le rôle est imposé.
  const initialRole = !referralCode && searchParams.get('role') === 'ORG_OWNER' ? 'ORG_OWNER' : 'EXTRA';
  const [role, setRole] = useState<'EXTRA' | 'ORG_OWNER'>(initialRole);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const user = await register({ email, password, firstName, lastName, role, referralCode: role === 'EXTRA' ? referralCode : undefined });
      navigate(dashboardPathFor(user.role));
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'EMAIL_TAKEN' ? 'Cet email est déjà utilisé.' : 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="app-shell">
      <div className="screen-content" style={{ justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center', marginBottom: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <Logo size={40} withText={false} />
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--accent-dark)' }}>Mon Extra</div>
          <div style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>Créez votre compte</div>
        </div>

        {referralCode && (
          <div className="badge badge-available" style={{ justifyContent: 'center', padding: '10px 14px', fontSize: 12.5 }}>
            Vous rejoignez Mon Extra sur invitation d'un apporteur d'affaires
          </div>
        )}

        {!referralCode && <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className={role === 'EXTRA' ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setRole('EXTRA')}
          >
            Je suis un extra
          </button>
          <button
            type="button"
            className={role === 'ORG_OWNER' ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setRole('ORG_OWNER')}
          >
            Je suis un établissement
          </button>
        </div>}

        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && <div className="error-banner">{error}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label className="field-label" htmlFor="firstName">Prénom</label>
              <input id="firstName" className="input" required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label className="field-label" htmlFor="lastName">Nom</label>
              <input id="lastName" className="input" required value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="field-label" htmlFor="email">Email</label>
            <input id="email" className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          </div>
          <div>
            <label className="field-label" htmlFor="password">Mot de passe</label>
            <input
              id="password"
              className="input"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Création…' : 'Créer mon compte'}
          </button>
        </form>

        <div style={{ textAlign: 'center', fontSize: 13.5, color: 'var(--text-secondary)' }}>
          Déjà un compte ? <Link to="/login" style={{ color: 'var(--accent-dark)', fontWeight: 700 }}>Se connecter</Link>
        </div>
      </div>
    </div>
  );
}
